'use client';

import { type HTMLAttributes, useEffect, useRef } from 'react';

interface Props {
  /** Sizing + slide-transform attrs handed in by `PhotoView`'s `render`. */
  attrs: Partial<HTMLAttributes<HTMLElement>>;
  src: string;
  poster: string | undefined;
}

/**
 * A DM video message playing inside the `react-photo-view` lightbox. A media
 * bubble carries a single video, so this is the lone slide in its message's
 * `PhotoProvider` — it mounts only while the lightbox is open (react-photo-view
 * renders slide content on demand) and unmounts on close, stopping playback.
 * The lightbox opens from a user tap, so `play()` is allowed; if a browser still
 * blocks it, the native controls start it.
 *
 * The stage box (from `attrs`) is pre-fitted to the viewport by the caller, so
 * `h-full w-full object-contain` fills it exactly without overflowing.
 */
export function LightboxVideoSlide({ attrs, src, poster }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    void el.play().catch(() => {
      // Autoplay blocked — the controls let the viewer start it.
    });
  }, []);

  return (
    <div {...attrs}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        className="h-full w-full object-contain"
      />
    </div>
  );
}
